import { PROJECTS_URL } from '../constants'
import baseQueryWithReauth from './baseQuery'
import { CreateProjectResp } from '../types/api/api-project'
import { Project } from '../types/models/project'

import {
  ListProjectsResp,
  UploadProjectContentRequest,
  UploadProjectResp,
  UploadProjectTiffRequest,
} from '../types/api/api-project'

import { ModelTree } from '../types/models/model'
import { CreateProjectReq, ProjectQueryDateTime, ProjectQueryRegex } from '../types/api/api-project'
import { apiTags } from './apiTags'
import { SimplePaginationReq } from '../types/common'
import { createApi } from '@reduxjs/toolkit/query/react'

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Project],

  endpoints: (builder) => ({
    fetchProjects: builder.query<ListProjectsResp, SimplePaginationReq>({
      query: (params) => ({
        url: PROJECTS_URL,
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.Project, id: 'LIST' },
        ...(result?.projects?.map(({ id }) => ({
          type: apiTags.Project,
          id,
        })) || []),
      ],
    }),

    createProject: builder.mutation<CreateProjectResp, CreateProjectReq>({
      query: (projectData) => ({
        url: PROJECTS_URL,
        method: 'POST',
        body: projectData,
      }),
      invalidatesTags: (result) => [
        { type: apiTags.Project, id: 'LIST' },
        ...(result?.id ? [{ type: apiTags.Project, id: result.id }] : []),
      ],
    }),

    getProject: builder.query<Project, string>({
      query: (id) => `${PROJECTS_URL}/${id}`,
      providesTags: (result, error, id) => [{ type: apiTags.Project, id }],
      // keepUnusedDataFor: 60,
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg !== previousArg
      },
    }),

    updateProject: builder.mutation<Project, { id: string; data: Partial<Project> }>({
      query: ({ id, data }) => ({
        url: `${PROJECTS_URL}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: apiTags.Project, id }],
    }),

    deleteProject: builder.mutation<string, string>({
      query: (id) => ({
        url: `${PROJECTS_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: apiTags.Project, id },
        { type: apiTags.Project, id: 'LIST' },
      ],
    }),

    queryProjects: builder.query<
      { projects: Project[]; total: number },
      ProjectQueryRegex | ProjectQueryDateTime
    >({
      query: (queryParams) => ({
        url: `${PROJECTS_URL}/query`,
        method: 'POST',
        body: queryParams,
      }),
      providesTags: (result) => [
        { type: apiTags.Project, id: 'LIST' },
        ...(result?.projects?.map(({ id }) => ({
          type: apiTags.Project,
          id,
        })) || []),
      ],
    }),

    updateProjectProfile: builder.mutation<void, { id: string; file: File; random?: boolean }>({
      query: ({ id, file, random }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `${PROJECTS_URL}/${id}/profile`,
          method: 'POST',
          body: formData,
          params: { random },
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.Project, id },
        { type: apiTags.ProjectProfile, id },
      ],
    }),

    uploadProjectContent: builder.mutation<UploadProjectResp, UploadProjectContentRequest>({
      query: ({ upload_id, files, labels_format = 'ag2', metadata }) => {
        const formData = new FormData()
        files.forEach((file) => formData.append('files', file))
        return {
          url: `${PROJECTS_URL}/${upload_id}/upload`,
          method: 'POST',
          body: formData,
          params: {
            labels_format,
            metadata: JSON.stringify(metadata),
          },
        }
      },
      invalidatesTags: (result, error, { upload_id, datasetid }) => [
        { type: apiTags.Dataset, id: datasetid },
        { type: apiTags.DatasetList, id: 'LIST' },
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.AnnotationStats, id: datasetid },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Content, id: 'LIST' },
        { type: apiTags.Metadata, upload_id },
        { type: apiTags.Tag, id: 'LIST' },
        { type: apiTags.TagProperties, id: datasetid },
        { type: apiTags.Project, upload_id },
      ],
    }),

    uploadInit: builder.mutation<
      { upload_id: string },
      {
        projectId: string
        name: string
        labels_file?: string
        labels_format?: string
        labels?: File
      }
    >({
      query: ({ projectId, name, labels_file, labels_format, labels }) => {
        const formData = new FormData()
        if (labels) {
          formData.append('files', labels)
        }
        return {
          url: `${PROJECTS_URL}/${projectId}/upload/init`,
          method: 'POST',
          body: formData,
          params: {
            name,
            labels_file,
            labels_fmt: labels_format,
          },
        }
      },
    }),

    uploadTiff: builder.mutation<UploadProjectResp, UploadProjectTiffRequest>({
      query: ({ id, files, clip_size, metadata }) => {
        const formData = new FormData()
        files.forEach((file) => formData.append('files', file))
        return {
          url: `${PROJECTS_URL}/${id}/uploadTiff`,
          method: 'POST',
          body: formData,
          params: {
            clip_size,
            metadata: JSON.stringify(metadata),
          },
        }
      },
      invalidatesTags: (result, error, { id, datasetid }) => [
        { type: apiTags.Dataset, id: datasetid },
        { type: apiTags.DatasetList, id: 'LIST' },
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.AnnotationStats, id: datasetid },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Content, id: 'LIST' },
        { type: apiTags.Metadata, id },
        { type: apiTags.Tag, id: 'LIST' },
        { type: apiTags.TagProperties, id: datasetid },
      ],
    }),

    getModelTree: builder.query<ModelTree, string>({
      query: (projectId) => ({
        url: `${PROJECTS_URL}/${projectId}/modeltree`,
        method: 'GET',
      }),
      providesTags: (result, error, projectId) => [
        { type: apiTags.Project, id: projectId },
        { type: apiTags.Model, id: 'LIST' },
      ],
    }),

    vectorizeProject: builder.mutation<void, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `${PROJECTS_URL}/${projectId}/vectorize`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: apiTags.Project, id: projectId },
        { type: apiTags.Content, id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useFetchProjectsQuery,
  useCreateProjectMutation,
  useGetProjectQuery,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useQueryProjectsQuery,
  useUpdateProjectProfileMutation,
  useUploadProjectContentMutation,
  useUploadTiffMutation,
  useUploadInitMutation,
  useGetModelTreeQuery,
  useVectorizeProjectMutation,
} = projectsApi
